import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { hasAllPermissions, hasAnyPermission } from '@nwm-oss/auth-core/permissions';
import { useFrontendAuth } from './session.js';
import { ForbiddenPage } from './shared-pages.js';

export function ProtectedRoute({
  children,
  anyOf,
  allOf,
  fallback,
  bypassPermissionsWhenEmbedded = true,
}: {
  readonly children: React.ReactNode;
  readonly anyOf?: readonly string[];
  readonly allOf?: readonly string[];
  readonly fallback?: React.ReactNode;
  readonly bypassPermissionsWhenEmbedded?: boolean;
}) {
  const auth = useFrontendAuth();
  const location = useLocation();
  const [loginError, setLoginError] = useState<string | null>(null);
  const [loginStarted, setLoginStarted] = useState(false);

  async function startLogin() {
    if (!auth.login || loginStarted) return;
    setLoginStarted(true);
    setLoginError(null);
    try {
      await auth.login(`${location.pathname}${location.search}`);
    } catch (caught) {
      setLoginStarted(false);
      setLoginError(caught instanceof Error ? caught.message : 'Sign-in could not be started.');
    }
  }

  useEffect(() => {
    if (
      auth.runtime === 'standalone-local'
      && !auth.isLoading
      && !auth.isAuthenticated
      && auth.login
    ) {
      void startLogin();
    }
  }, [auth, location.pathname, location.search]);

  if (auth.isLoading) {
    return <main className="content"><section className="panel"><p>Verifying session...</p></section></main>;
  }

  if (!auth.isAuthenticated) {
    return (
      <section className="panel auth-state" role="status" aria-live="polite">
        <h1>Sign in to continue</h1>
        <p>{loginError ?? (loginStarted ? 'Redirecting to secure sign-in…' : 'Your session is not available.')}</p>
        {loginError || !loginStarted ? (
          <button type="button" onClick={() => void startLogin()} disabled={!auth.login || loginStarted}>
            {loginStarted ? 'Starting sign-in…' : 'Sign in'}
          </button>
        ) : null}
      </section>
    );
  }

  if (bypassPermissionsWhenEmbedded && auth.runtime === 'embedded') {
    return <>{children}</>;
  }

  const permissions = auth.identity?.permissions ?? [];
  const satisfiesAll = !allOf || hasAllPermissions(permissions, allOf);
  const satisfiesAny = !anyOf || hasAnyPermission(permissions, anyOf);

  if (!satisfiesAll || !satisfiesAny) {
    return <>{fallback ?? <ForbiddenPage />}</>;
  }

  return <>{children}</>;
}
